import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderKanban,
  Wrench,
  Images,
  MessageSquareQuote,
  Inbox,
  ArrowUpRight,
  Clock,
  ExternalLink,
  AlertCircle,
  Package,
  Layers,
  Phone,
  Mail,
  MessageCircle,
  RefreshCw,
  FolderOpen,
  Settings,
  Plus,
} from 'lucide-react';
import StatCard from '../components/StatCard';
import Badge from '../components/Badge';
import EmptyState from '../components/EmptyState';
import { queries } from '../../lib/supabase/queries';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState({
    totalProjects: 0,
    publishedProjects: 0,
    draftProjects: 0,
    totalServices: 0,
    totalPackages: 0,
    totalGallery: 0,
    totalTestimonials: 0,
    newEnquiries: 0,
    inProgressEnquiries: 0,
    recentEnquiries: [],
    recentProjects: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMetrics = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError(
        'Supabase connection is not configured in .env. Please configure REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to load live database metrics.'
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.getDashboardMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError(err.message || 'Failed to fetch live metrics from Supabase.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  return (
    <div className="space-y-8">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 p-6 sm:p-8 rounded-3xl border border-slate-800/80 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#9BB7A3]/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-[#9BB7A3]/10 text-[#9BB7A3] border border-[#9BB7A3]/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#9BB7A3] animate-pulse"></span>
              Live Supabase Production DB
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            VJ DSIGN STUDIO — Control Center
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">
            Real-time management metrics, active client leads, and live portfolio content controls.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 relative z-10">
          <button
            onClick={loadMetrics}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition"
            title="Refresh Metrics"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Sync</span>
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition"
          >
            <span>Live Website</span>
            <ExternalLink size={14} />
          </a>
          <Link
            to="/admin/projects"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 text-xs font-bold transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95"
          >
            <Plus size={15} />
            <span>New Project</span>
          </Link>
        </div>
      </div>

      {/* Quick Action Navigation Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Link
          to="/admin/projects"
          className="p-3.5 bg-slate-900/70 hover:bg-slate-800/90 border border-slate-800/80 hover:border-[#9BB7A3]/40 rounded-2xl transition flex items-center gap-2.5 text-xs font-bold text-slate-300 hover:text-white group"
        >
          <div className="w-8 h-8 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center shrink-0 group-hover:scale-110 transition">
            <FolderKanban size={16} />
          </div>
          <span className="truncate">Projects</span>
        </Link>

        <Link
          to="/admin/services"
          className="p-3.5 bg-slate-900/70 hover:bg-slate-800/90 border border-slate-800/80 hover:border-[#9BB7A3]/40 rounded-2xl transition flex items-center gap-2.5 text-xs font-bold text-slate-300 hover:text-white group"
        >
          <div className="w-8 h-8 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center shrink-0 group-hover:scale-110 transition">
            <Wrench size={16} />
          </div>
          <span className="truncate">Services</span>
        </Link>

        <Link
          to="/admin/packages"
          className="p-3.5 bg-slate-900/70 hover:bg-slate-800/90 border border-slate-800/80 hover:border-[#9BB7A3]/40 rounded-2xl transition flex items-center gap-2.5 text-xs font-bold text-slate-300 hover:text-white group"
        >
          <div className="w-8 h-8 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center shrink-0 group-hover:scale-110 transition">
            <Package size={16} />
          </div>
          <span className="truncate">Packages</span>
        </Link>

        <Link
          to="/admin/media"
          className="p-3.5 bg-slate-900/70 hover:bg-slate-800/90 border border-slate-800/80 hover:border-[#9BB7A3]/40 rounded-2xl transition flex items-center gap-2.5 text-xs font-bold text-slate-300 hover:text-white group"
        >
          <div className="w-8 h-8 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center shrink-0 group-hover:scale-110 transition">
            <FolderOpen size={16} />
          </div>
          <span className="truncate">Media Library</span>
        </Link>

        <Link
          to="/admin/enquiries"
          className="p-3.5 bg-slate-900/70 hover:bg-slate-800/90 border border-slate-800/80 hover:border-[#9BB7A3]/40 rounded-2xl transition flex items-center gap-2.5 text-xs font-bold text-slate-300 hover:text-white group relative"
        >
          <div className="w-8 h-8 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center shrink-0 group-hover:scale-110 transition">
            <Inbox size={16} />
          </div>
          <span className="truncate">Enquiries</span>
          {metrics.newEnquiries > 0 && (
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rose-500 animate-ping"></span>
          )}
        </Link>

        <Link
          to="/admin/settings"
          className="p-3.5 bg-slate-900/70 hover:bg-slate-800/90 border border-slate-800/80 hover:border-[#9BB7A3]/40 rounded-2xl transition flex items-center gap-2.5 text-xs font-bold text-slate-300 hover:text-white group"
        >
          <div className="w-8 h-8 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center shrink-0 group-hover:scale-110 transition">
            <Settings size={16} />
          </div>
          <span className="truncate">Site Settings</span>
        </Link>
      </div>

      {/* Error / Missing Configuration Banner */}
      {error && (
        <div className="p-5 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-sm flex items-start gap-3.5">
          <AlertCircle size={20} className="shrink-0 mt-0.5 text-rose-400" />
          <div>
            <p className="font-bold mb-1">Database Connection Status</p>
            <p className="text-rose-300/80 text-xs leading-relaxed">{error}</p>
          </div>
        </div>
      )}

      {/* Metric Cards Grid - All numbers directly from Supabase SQL counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Total Projects"
          value={loading ? '...' : metrics.totalProjects}
          subtitle={`${metrics.publishedProjects} Published · ${metrics.draftProjects} Drafts`}
          icon={<FolderKanban size={22} />}
        />
        <StatCard
          title="Active Services"
          value={loading ? '...' : metrics.totalServices}
          subtitle="Studio service offerings"
          icon={<Wrench size={22} />}
        />
        <StatCard
          title="Pricing Packages"
          value={loading ? '...' : metrics.totalPackages}
          subtitle="Pricing suites in database"
          icon={<Package size={22} />}
        />
        <StatCard
          title="New Enquiries"
          value={loading ? '...' : metrics.newEnquiries}
          subtitle={`${metrics.inProgressEnquiries} In Progress Leads`}
          icon={<Inbox size={22} />}
          trend={metrics.newEnquiries > 0 ? `${metrics.newEnquiries} Action Required` : undefined}
          trendUp={metrics.newEnquiries > 0}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Gallery Marquee Items"
          value={loading ? '...' : metrics.totalGallery}
          subtitle="Showcase images in database"
          icon={<Images size={22} />}
        />
        <StatCard
          title="Client Testimonials"
          value={loading ? '...' : metrics.totalTestimonials}
          subtitle="Published 5-star reviews"
          icon={<MessageSquareQuote size={22} />}
        />
        <StatCard
          title="Live Published Projects"
          value={loading ? '...' : metrics.publishedProjects}
          subtitle="Visible on public website"
          icon={<Layers size={22} />}
        />
      </div>

      {/* Tables: Recent Enquiries & Recent Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Enquiries */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-base font-bold text-white flex items-center gap-2">
                  <Inbox size={18} className="text-[#9BB7A3]" />
                  Recent Enquiries & Leads
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">Real customer inquiries from contact form</p>
              </div>
              <Link
                to="/admin/enquiries"
                className="text-xs font-semibold text-[#9BB7A3] hover:underline inline-flex items-center gap-1"
              >
                View All <ArrowUpRight size={14} />
              </Link>
            </div>

            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="w-8 h-8 border-2 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
              </div>
            ) : metrics.recentEnquiries.length === 0 ? (
              <EmptyState
                title="No Enquiries Yet"
                description="Customer inquiries submitted through your website contact form will appear here in real time."
              />
            ) : (
              <div className="space-y-3">
                {metrics.recentEnquiries.map((enq) => {
                  const cleanPhone = (enq.phone || '').replace(/[^0-9]/g, '');
                  const whatsappUrl = cleanPhone
                    ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(
                        `Hi ${enq.name}, thank you for reaching out to VJ Dsign Studio!`
                      )}`
                    : null;

                  return (
                    <div
                      key={enq.id}
                      className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition space-y-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-sm text-slate-100">{enq.name}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{enq.email}</div>
                        </div>
                        <Badge
                          variant={
                            enq.status === 'new'
                              ? 'warning'
                              : enq.status === 'completed'
                              ? 'success'
                              : 'info'
                          }
                        >
                          {enq.status.toUpperCase()}
                        </Badge>
                      </div>

                      <p className="text-xs text-slate-300 line-clamp-2 bg-slate-900/60 p-2.5 rounded-xl border border-slate-800/50">
                        {enq.subject ? <strong className="text-[#9BB7A3]">{enq.subject}: </strong> : ''}
                        {enq.message}
                      </p>

                      <div className="flex items-center justify-between pt-1 border-t border-slate-800/60 text-[11px] text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {new Date(enq.created_at).toLocaleDateString()}
                        </span>

                        {/* Direct Response Action Buttons */}
                        <div className="flex items-center gap-1.5">
                          {enq.phone && (
                            <a
                              href={`tel:${enq.phone}`}
                              className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition"
                              title="Call Client"
                            >
                              <Phone size={13} />
                            </a>
                          )}
                          {whatsappUrl && (
                            <a
                              href={whatsappUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-lg bg-emerald-950/80 hover:bg-emerald-600 text-emerald-400 hover:text-white transition"
                              title="Chat on WhatsApp"
                            >
                              <MessageCircle size={13} />
                            </a>
                          )}
                          {enq.email && (
                            <a
                              href={`mailto:${enq.email}?subject=VJ Dsign Studio - Inquiry Response`}
                              className="p-1.5 rounded-lg bg-slate-800 hover:bg-[#9BB7A3] text-slate-300 hover:text-slate-950 transition"
                              title="Send Email"
                            >
                              <Mail size={13} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent Projects */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-base font-bold text-white flex items-center gap-2">
                  <FolderKanban size={18} className="text-[#9BB7A3]" />
                  Recent Projects
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">Latest project records in Supabase</p>
              </div>
              <Link
                to="/admin/projects"
                className="text-xs font-semibold text-[#9BB7A3] hover:underline inline-flex items-center gap-1"
              >
                View All <ArrowUpRight size={14} />
              </Link>
            </div>

            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="w-8 h-8 border-2 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
              </div>
            ) : metrics.recentProjects.length === 0 ? (
              <EmptyState
                title="No Projects Added"
                description="No project records found in your database. Click '+ New Project' to create your first portfolio entry."
              />
            ) : (
              <div className="space-y-3">
                {metrics.recentProjects.map((proj) => (
                  <div
                    key={proj.id}
                    className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {proj.cover_image_url ? (
                        <img
                          src={proj.cover_image_url}
                          alt={proj.title}
                          className="w-12 h-12 rounded-xl object-cover shrink-0 border border-slate-800"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-slate-800 shrink-0 flex items-center justify-center text-slate-500 font-bold text-xs">
                          IMG
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-slate-100 truncate">{proj.title}</div>
                        <div className="text-xs text-slate-400 truncate">{proj.category || 'General'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        variant={
                          proj.status === 'published'
                            ? 'success'
                            : proj.status === 'draft'
                            ? 'warning'
                            : 'default'
                        }
                      >
                        {proj.status.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
